import { useDeferredValue, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import {
  CHALLENGE_PROJECT_DRAFT_PATH,
  CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH,
  CHALLENGE_PROJECT_SUBMISSION_PATH,
} from '../../../shared/challengeProjectSeed';
import { CREDIT_PAYMENT_URL } from '../../../shared/billing';
import { localizeChallenge } from './challengeLocalization';
import {
  countNonEmptyLines,
  countWords,
  formatConstraintLabel,
  formatSubmissionStatus,
  formatTierLabel,
} from './challengeUtils';
import useEnsureLaunchChallenges from './useEnsureLaunchChallenges';
import './challenges.css';

function formatProjectFileDate(value, language) {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat(language || 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return new Date(value).toLocaleString();
  }
}

export default function ChallengeWritePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const detail = useQuery(api.challenges.getBySlug, slug ? { slug } : 'skip');
  useEnsureLaunchChallenges({
    enabled: detail !== undefined,
    empty: detail === null,
  });
  const submission = useQuery(
    api.challenges.getUserSubmission,
    detail?.challenge?._id ? { challengeId: detail.challenge._id } : 'skip',
  );
  const startProjectFromChallenge = useMutation(api.challenges.startProjectFromChallenge);
  const submitEntry = useMutation(api.challenges.submitEntry);
  const withdrawSubmission = useMutation(api.challenges.withdrawSubmission);
  const [projectLaunchMode, setProjectLaunchMode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState('info');

  const challenge = localizeChallenge(detail?.challenge, t);
  const linkedProjectId = submission?.projectId || detail?.viewer?.linkedProjectId || null;
  const isEditable = submission?.status === 'draft' || submission?.status === 'rejected';
  const canonicalSubmissionFile = useQuery(
    api.files.getByPath,
    linkedProjectId
      ? { projectId: linkedProjectId, path: CHALLENGE_PROJECT_SUBMISSION_PATH }
      : 'skip',
  );
  const legacySubmissionFile = useQuery(
    api.files.getByPath,
    linkedProjectId
      ? { projectId: linkedProjectId, path: CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH }
      : 'skip',
  );
  const draftFile = useQuery(
    api.files.getByPath,
    linkedProjectId
      ? { projectId: linkedProjectId, path: CHALLENGE_PROJECT_DRAFT_PATH }
      : 'skip',
  );
  const submissionFile = canonicalSubmissionFile || legacySubmissionFile;
  const submissionPath = canonicalSubmissionFile ? CHALLENGE_PROJECT_SUBMISSION_PATH : CHALLENGE_PROJECT_LEGACY_SUBMISSION_PATH;
  const submissionContent = submissionFile?.content ?? '';
  const deferredSubmissionContent = useDeferredValue(submissionContent);
  const validation = useQuery(
    api.challenges.validateSubmission,
    detail?.challenge?._id && linkedProjectId && isEditable
      ? { challengeId: detail.challenge._id, content: deferredSubmissionContent }
      : 'skip',
  );

  if (detail === undefined || submission === undefined || (linkedProjectId && (canonicalSubmissionFile === undefined || legacySubmissionFile === undefined))) {
    return (
      <div className="chw-shell">
        <div className="chw-card">
          <h2>{t('challengesRuntime.write.loadingTitle', { defaultValue: 'Loading challenge editor…' })}</h2>
          <p>{t('challengesRuntime.write.loadingSubtitle', { defaultValue: 'Preparing the project-backed submission workspace.' })}</p>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="chw-shell">
        <div className="chw-card">
          <h2>{t('challengesRuntime.detail.notFoundTitle', { defaultValue: 'Challenge not found' })}</h2>
          <button className="btn btn-primary" onClick={() => navigate('/challenges')}>
            {t('challengesRuntime.actions.backToChallenges', { defaultValue: 'Back to challenges' })}
          </button>
        </div>
      </div>
    );
  }

  if (!detail.viewer.canWrite) {
    return (
      <div className="chw-shell">
        <div className="chw-card">
          <h2>{challenge.title}</h2>
          <p>
            {t('challengesRuntime.write.tierRequiredToOpen', {
              defaultValue: '{{tier}} is required before you can open this draft.',
              tier: formatTierLabel(detail.challenge.requiredTier, t),
            })}
          </p>
          <div className="chw-actions">
            <Link to={`/challenges/${detail.challenge.slug}`} className="btn btn-secondary">
              {t('challengesRuntime.actions.backToChallenge', { defaultValue: 'Back to challenge' })}
            </Link>
            <a href={CREDIT_PAYMENT_URL} className="btn btn-primary">
              {t('challengesRuntime.actions.viewPlans', { defaultValue: 'View plans' })}
            </a>
          </div>
        </div>
      </div>
    );
  }

  const handleLaunchProject = async (destination) => {
    if (!detail) return;
    setMessage('');
    setMessageTone('info');
    setProjectLaunchMode(destination);
    try {
      const result = await startProjectFromChallenge({ challengeId: detail.challenge._id });
      const path = destination === 'draft'
        ? CHALLENGE_PROJECT_DRAFT_PATH
        : CHALLENGE_PROJECT_SUBMISSION_PATH;
      if (destination === 'project') {
        navigate(`/projects/${result.projectId}`);
      } else {
        navigate(`/projects/${result.projectId}/editor?path=${encodeURIComponent(path)}`);
      }
    } catch (error) {
      setMessageTone('error');
      setMessage(
        error?.data?.message
          || error?.message
          || t('challengesRuntime.write.projectLaunchFailed', {
            defaultValue: 'Could not open this challenge as a project.',
          }),
      );
    } finally {
      setProjectLaunchMode('');
    }
  };

  const handleSubmit = async () => {
    if (!submission) return;
    setIsSubmitting(true);
    setMessage('');
    setMessageTone('info');
    try {
      await submitEntry({ submissionId: submission._id });
      navigate(`/challenges/${slug}/submission`, { replace: true });
    } catch (error) {
      setMessageTone('error');
      setMessage(
        error?.data?.message
          || error?.message
          || t('challengesRuntime.write.submissionFailed', { defaultValue: 'Submission failed.' }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    if (!submission) return;
    setMessage('');
    setMessageTone('info');
    try {
      await withdrawSubmission({ submissionId: submission._id });
      setMessage(t('challengesRuntime.write.withdrawSuccess', { defaultValue: 'Submission moved back to draft.' }));
    } catch (error) {
      setMessageTone('error');
      setMessage(error?.data?.message || error?.message || t('challengesRuntime.write.withdrawFailed', { defaultValue: 'Could not withdraw submission.' }));
    }
  };

  if (!submission || !linkedProjectId) {
    return (
      <div className="chw-shell">
        <div className="chw-card">
          <h2>{challenge.title}</h2>
          <p>
            {t('challengesRuntime.write.projectModelBody', {
              defaultValue: 'Challenge submissions now live inside a dedicated project. Create the challenge project, write your final text in challenge-submission.md, then submit from here.',
            })}
          </p>
          {message ? <div className="chw-banner error" style={{ marginTop: 16 }}>{message}</div> : null}
          <div className="chw-actions">
            <Link to={`/challenges/${detail.challenge.slug}`} className="btn btn-secondary">
              {t('challengesRuntime.actions.backToChallenge', { defaultValue: 'Back to challenge' })}
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => handleLaunchProject('submission')}
              disabled={projectLaunchMode === 'submission'}
            >
              {projectLaunchMode === 'submission'
                ? t('challengesRuntime.write.launchingProject', { defaultValue: 'Opening project…' })
                : t('challengesRuntime.write.createChallengeProject', { defaultValue: 'Create challenge project' })}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handleLaunchProject('draft')}
              disabled={projectLaunchMode === 'draft'}
            >
              {projectLaunchMode === 'draft'
                ? t('challengesRuntime.write.launchingMentor', { defaultValue: 'Opening mentor…' })
                : t('challengesRuntime.write.openScratchDraft', { defaultValue: 'Open scratch draft' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isEditable) {
    return <Navigate to={`/challenges/${slug}/submission`} replace />;
  }

  const validationWordCount = validation?.wordCount ?? countWords(submissionContent);
  const validationLineCount = validation?.lineCount ?? countNonEmptyLines(submissionContent);
  const hasContent = submissionContent.trim().length > 0;
  const canSubmit = Boolean(submission && linkedProjectId && submissionFile && validation?.valid && hasContent && !isSubmitting);
  const validationMessages = !validation?.valid
    ? validation?.errors?.length
      ? validation.errors
      : detail?.challenge?.constraintType !== 'none'
        ? [
            t('challengesRuntime.write.constraintError', {
              defaultValue: 'Entry must follow this rule: {{constraint}}.',
              constraint: formatConstraintLabel(challenge, t),
            }),
          ]
        : []
    : [];

  return (
    <div className="chw-shell">
      <div className="chw-header">
        <div>
          <div className="chw-breadcrumbs">
            <Link to="/challenges">{t('landing.nav.challenges', { defaultValue: 'Challenges' })}</Link>
            <span>/</span>
            <Link to={`/challenges/${challenge.slug}`}>{challenge.title}</Link>
            <span>/</span>
            <span>{t('challengesRuntime.write.breadcrumb', { defaultValue: 'Write' })}</span>
          </div>
          <h1>{challenge.title}</h1>
          <p>{challenge.description}</p>
        </div>
        <div className="chw-header-actions">
          <Link to={`/challenges/${challenge.slug}/feed`} className="btn btn-secondary">
            {t('challengesRuntime.actions.viewFeed', { defaultValue: 'View feed' })}
          </Link>
        </div>
      </div>

      {message ? <div className={`chw-banner ${messageTone === 'error' ? 'error' : ''}`}>{message}</div> : null}

      <div className="chw-grid">
        <section className="chw-editor-panel">
          <div className="chw-submit-guide">
            <div>
              <div className="chw-submission-preview-label">
                {t('challengesRuntime.write.submitGuideLabel', { defaultValue: 'How submission works' })}
              </div>
              <div className={`chw-validation-pill ${validation?.valid ? 'valid' : 'invalid'}`}>
                {validation?.valid
                  ? t('challengesRuntime.write.readyToSubmit', { defaultValue: 'Ready to submit' })
                  : t('challengesRuntime.write.fixBeforeSubmit', { defaultValue: 'Fix before submit' })}
              </div>
              <p>
                {t('challengesRuntime.write.projectSubmitGuideBody', {
                  defaultValue: 'Edit challenge-submission.md inside the project, keep challenge-draft.md for exploratory work, then submit the project from here. The admin review will read your final submission file and the linked project workflow.',
                })}
              </p>
              {!validation?.valid && validationMessages.length ? (
                <ul className="chw-validation-list">
                  {validationMessages.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              ) : null}
            </div>
            <button className="btn btn-primary chw-submit-primary" onClick={handleSubmit} disabled={!canSubmit}>
              {isSubmitting
                ? t('challengesRuntime.write.submitting', { defaultValue: 'Submitting…' })
                : t('challengesRuntime.write.submitProjectEntry', { defaultValue: 'Submit project entry' })}
            </button>
          </div>

          <div className="chw-project-actions">
            <Link
              to={`/projects/${linkedProjectId}/editor?path=${encodeURIComponent(submissionPath)}`}
              className="btn btn-primary"
            >
              {t('challengesRuntime.write.openSubmissionFile', { defaultValue: 'Open challenge-submission.md' })}
            </Link>
            <Link
              to={`/projects/${linkedProjectId}/editor?path=${encodeURIComponent(CHALLENGE_PROJECT_DRAFT_PATH)}`}
              className="btn btn-secondary"
            >
              {t('challengesRuntime.write.openScratchDraft', { defaultValue: 'Open scratch draft' })}
            </Link>
            <Link to={`/projects/${linkedProjectId}`} className="btn btn-secondary">
              {t('challengesRuntime.actions.openProjectHome', { defaultValue: 'Open project home' })}
            </Link>
            <Link to={`/projects/${linkedProjectId}/reader-preview`} className="btn btn-secondary">
              {t('projects.stages.openReaderPreview', { defaultValue: 'Open Reader Preview' })}
            </Link>
            <Link to={`/projects/${linkedProjectId}/draft-review`} className="btn btn-secondary">
              {t('projects.stages.runDraftReview', { defaultValue: 'Run Draft Review' })}
            </Link>
          </div>

          <div className="chw-source-card">
            <div className="chw-submission-preview-label">
              {t('challengesRuntime.write.submissionFileLabel', { defaultValue: 'Final submission file' })}
            </div>
            <strong>{submissionPath}</strong>
            <p>
              {t('challengesRuntime.write.submissionFileBody', {
                defaultValue: 'Whatever is written in this file is what gets submitted for the challenge.',
              })}
            </p>
            <div className="chw-file-meta">
              <span>{t('challengesRuntime.write.wordCount', { defaultValue: 'Word count' })}: {validationWordCount}</span>
              <span>{t('challengesRuntime.write.lineCount', { defaultValue: 'Line count' })}: {validationLineCount}</span>
              {submissionFile?.updatedAt ? (
                <span>
                  {t('challengesRuntime.write.lastEdited', {
                    defaultValue: 'Last edited: {{date}}',
                    date: formatProjectFileDate(submissionFile.updatedAt, i18n.language),
                  })}
                </span>
              ) : null}
            </div>
          </div>

          <div className="chw-submission-preview">
            <div className="chw-submission-preview-label">
              {t('challengesRuntime.write.previewLabel', { defaultValue: 'Submission preview' })}
            </div>
            {hasContent ? (
              <pre className="chw-submission-content">{submissionContent}</pre>
            ) : (
              <p className="chw-empty-preview">
                {t('challengesRuntime.write.previewEmpty', {
                  defaultValue: 'challenge-submission.md is still empty. Open the file and place your final challenge text there before submitting.',
                })}
              </p>
            )}
          </div>
        </section>

        <aside className="chw-sidebar">
          <div className="chw-sidebar-grid">
            <div className="chw-card">
              <span className="ch-section-label">{t('challengesRuntime.labels.constraint', { defaultValue: 'Constraint' })}</span>
              <strong>{formatConstraintLabel(challenge, t)}</strong>
            </div>
            <div className="chw-card">
              <span className="ch-section-label">{t('challengesRuntime.write.wordCount', { defaultValue: 'Word count' })}</span>
              <strong>{validationWordCount}</strong>
            </div>
            <div className="chw-card">
              <span className="ch-section-label">{t('challengesRuntime.write.lineCount', { defaultValue: 'Line count' })}</span>
              <strong>{validationLineCount}</strong>
            </div>
            <div className="chw-card chw-card-span-2">
              <span className="ch-section-label">{t('challengesRuntime.write.constraintStatus', { defaultValue: 'Constraint status' })}</span>
              <strong>
                {validation?.valid
                  ? t('challengesRuntime.write.constraintReady', { defaultValue: 'Matches the challenge rule' })
                  : t('challengesRuntime.write.constraintNeedsRevision', { defaultValue: 'Needs revision before submission' })}
              </strong>
            </div>
            <div className="chw-card chw-card-span-2">
              <span className="ch-section-label">{t('challengesRuntime.write.currentStatus', { defaultValue: 'Current status' })}</span>
              <strong>{submission ? formatSubmissionStatus(submission.status, t) : t('challengesRuntime.submissionStatuses.draft', { defaultValue: 'Draft' })}</strong>
            </div>
          </div>

          <div className="chw-card chw-card-wide">
            <span className="ch-section-label">{t('challengesRuntime.write.projectWorkflowLabel', { defaultValue: 'Challenge project workflow' })}</span>
            <strong>{t('challengesRuntime.write.projectWorkflowReady', { defaultValue: 'Project-backed submission ready' })}</strong>
            <p style={{ marginTop: 10 }}>
              {t('challengesRuntime.write.projectWorkflowBody', {
                defaultValue: 'Use challenge-draft.md for rough work, notes, and alternates. Use challenge-submission.md for the final text you want reviewed and published.',
              })}
            </p>
            <div className="chw-actions" style={{ justifyContent: 'flex-start' }}>
              <Link to={`/projects/${linkedProjectId}/editor?path=${encodeURIComponent(CHALLENGE_PROJECT_DRAFT_PATH)}`} className="btn btn-secondary">
                {t('challengesRuntime.actions.openMentorWorkspace', { defaultValue: 'Open mentor workspace' })}
              </Link>
              <Link to={`/projects/${linkedProjectId}/editor?path=${encodeURIComponent(submissionPath)}`} className="btn btn-secondary">
                {t('challengesRuntime.write.openSubmissionFile', { defaultValue: 'Open challenge-submission.md' })}
              </Link>
              <Link to={`/projects/${linkedProjectId}/reader-preview`} className="btn btn-secondary">
                {t('projects.stages.openReaderPreview', { defaultValue: 'Open Reader Preview' })}
              </Link>
              <Link to={`/projects/${linkedProjectId}/draft-review`} className="btn btn-secondary">
                {t('projects.stages.runDraftReview', { defaultValue: 'Run Draft Review' })}
              </Link>
            </div>
          </div>

          {draftFile?.updatedAt ? (
            <div className="chw-card chw-card-wide">
              <span className="ch-section-label">{t('challengesRuntime.write.draftWorkspaceLabel', { defaultValue: 'Scratch draft workspace' })}</span>
              <strong>{CHALLENGE_PROJECT_DRAFT_PATH}</strong>
              <p style={{ marginTop: 10 }}>
                {t('challengesRuntime.write.draftWorkspaceBody', {
                  defaultValue: 'Keep scenes, alternates, and mentor-driven experiments in the draft file. The admin review will still focus on challenge-submission.md as the official entry.',
                })}
              </p>
              <small>{formatProjectFileDate(draftFile.updatedAt, i18n.language)}</small>
            </div>
          ) : null}
        </aside>
      </div>

      <div className="chw-mobile-submit-bar">
        <div>
          <span>{t('challengesRuntime.write.currentStatus', { defaultValue: 'Current status' })}</span>
          <strong>
            {validation?.valid
              ? t('challengesRuntime.write.readyToSubmit', { defaultValue: 'Ready to submit' })
              : t('challengesRuntime.write.fixBeforeSubmit', { defaultValue: 'Fix before submit' })}
          </strong>
        </div>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting
            ? t('challengesRuntime.write.submitting', { defaultValue: 'Submitting…' })
            : t('challengesRuntime.actions.submitEntry', { defaultValue: 'Submit entry' })}
        </button>
      </div>
    </div>
  );
}
